import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, Users, TrendingUp, Clock, PoundSterling, Download, Search,
  MailCheck, LifeBuoy, RefreshCw, Percent, ShoppingCart,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VideraMark } from "@/components/VideraMark";
import { toast } from "sonner";

const BUCKETS = [
  { key: "", label: "All" },
  { key: "trialing", label: "On trial" },
  { key: "paid", label: "Paying" },
  { key: "trial_expired", label: "Trial expired" },
  { key: "cancelled", label: "Cancelled" },
  { key: "internal", label: "Internal" },
];

const BUCKET_STYLE = {
  trialing: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  trial_expired: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  internal: "bg-sky-50 text-sky-700 border-sky-200",
};

const money = (n) => `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
const day = (v) => (v ? String(v).slice(0, 10) : "—");

const RECOVERY_STATE = {
  recovered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  chased: "bg-amber-50 text-amber-700 border-amber-200",
  cold: "bg-slate-100 text-slate-600 border-slate-200",
};

const Stat = ({ icon: Icon, label, value, sub, testId, tone = "slate" }) => (
  <div data-testid={testId} className="panel px-5 py-4">
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</span>
      <Icon size={15} className={`text-${tone}-400`} />
    </div>
    <p className="mt-2 text-3xl font-bold tabular-nums text-slate-900">{value}</p>
    {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
  </div>
);

export default function PlatformConsole() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [rows, setRows] = useState([]);
  const [bucket, setBucket] = useState("");
  const [query, setQuery] = useState("");
  const [includeTest, setIncludeTest] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [{ data: o }, { data: w }] = await Promise.all([
        api.get("/platform/overview", { params: { include_test: includeTest } }),
        api.get("/platform/workspaces", {
          params: { include_test: includeTest, bucket: bucket || undefined, q: query || undefined },
        }),
      ]);
      setOverview(o);
      setRows(w);
      api.get("/platform/stripe-status").then(({ data }) => setStripe(data)).catch(() => setStripe(null));
      api.get("/platform/recovery").then(({ data }) => setRecovery(data)).catch(() => setRecovery(null));
    } catch (e) {
      if (e.response?.status === 404 || e.response?.status === 401) return navigate("/dashboard");
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeTest, bucket]);

  const sendTest = async () => {
    try {
      const { data } = await api.post("/platform/test-email");
      toast.success(`Test email sent to ${data.sent_to}`);
      setEmailError(null);
    } catch (e) {
      const message = apiError(e.response?.data?.detail);
      setEmailError(message);
      toast.error(message);
    }
  };

  const s = overview?.summary;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-white/10 bg-slate-900/80 backdrop-blur">
        <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-4 px-6 py-4">
          <VideraMark size={20} className="text-amber-400" />
          <div className="mr-auto">
            <p className="text-sm font-semibold tracking-tight">Platform Console</p>
            <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Videra Technology — owner only</p>
          </div>
          <Button data-testid="platform-tickets-link" variant="outline" size="sm"
            onClick={() => navigate("/platform/tickets")}
            className="gap-1.5 border-white/15 bg-transparent text-slate-200 hover:bg-white/10 hover:text-white">
            <LifeBuoy size={14} />
            Support queue
            {overview?.tickets?.open ? (
              <span className="ml-1 rounded-full bg-amber-400 px-1.5 text-[11px] font-bold text-slate-900">
                {overview.tickets.open}
              </span>
            ) : null}
          </Button>
          <Button data-testid="platform-test-email-btn" variant="outline" size="sm" onClick={sendTest}
            className="gap-1.5 border-white/15 bg-transparent text-slate-200 hover:bg-white/10 hover:text-white">
            <MailCheck size={14} /> Send test email
          </Button>
          <Button data-testid="platform-back-btn" size="sm" onClick={() => navigate("/dashboard")}
            className="bg-white/10 text-slate-100 hover:bg-white/20">Back to app</Button>
        </div>
      </header>

      <main data-testid="platform-console" className="mx-auto max-w-[1500px] px-6 py-8">
        <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold sm:text-4xl">Client analytics</h1>
            <p className="mt-1 text-sm text-slate-400">
              Every workspace, who signed up, and how the trial funnel is converting.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label data-testid="platform-include-test" className="flex cursor-pointer items-center gap-2 text-xs text-slate-400">
              <input type="checkbox" checked={includeTest} className="accent-amber-400"
                onChange={(e) => setIncludeTest(e.target.checked)} />
              Show test workspaces
            </label>
            <Button variant="outline" size="sm" onClick={load} disabled={busy}
              className="gap-1.5 border-white/15 bg-transparent text-slate-200 hover:bg-white/10 hover:text-white">
              <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
            </Button>
            <a data-testid="platform-csv-link"
              href={`${API}/platform/workspaces.csv?include_test=${includeTest}`}
              onClick={(e) => {
                e.preventDefault();
                api.get("/platform/workspaces.csv", { params: { include_test: includeTest }, responseType: "blob" })
                  .then(({ data }) => {
                    const url = URL.createObjectURL(data);
                    const a = document.createElement("a");
                    a.href = url; a.download = "videra-workspaces.csv"; a.click();
                    URL.revokeObjectURL(url);
                  })
                  .catch(() => toast.error("Export failed"));
              }}>
              <Button size="sm" className="gap-1.5 bg-amber-400 text-slate-900 hover:bg-amber-300">
                <Download size={14} /> Export CSV
              </Button>
            </a>
          </div>
        </div>

        {stripe && (
          <div data-testid="platform-stripe-status"
            className={`mb-5 rounded-lg border px-4 py-3 text-sm ${
              stripe.can_take_real_payments
                ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-200"
                : "border-amber-400/30 bg-amber-400/10 text-amber-100"
            }`}>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
              <span className="font-semibold">
                Payments: {stripe.can_take_real_payments
                  ? "live and ready to take real money"
                  : stripe.mode === "live" ? "live keys, but not ready yet" : `${stripe.mode} mode — no real money`}
              </span>
              {stripe.account && (
                <span className="text-xs opacity-90">
                  {stripe.account.business_name || stripe.account.id} · {stripe.account.country} ·
                  {" "}charges {stripe.account.charges_enabled ? "on" : "off"} ·
                  {" "}details {stripe.account.details_submitted ? "submitted" : "not submitted"}
                </span>
              )}
              <span className="text-xs opacity-90">
                {stripe.prices.length} of {stripe.prices.length + stripe.missing_prices.length} plan prices found
                {stripe.webhook_secret_configured ? " · webhook secret set" : " · webhook secret MISSING"}
              </span>
            </div>
            {stripe.missing_prices.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <p className="text-xs opacity-90">
                  Missing prices: {stripe.missing_prices.join(", ")} — customers cannot pay until these exist.
                </p>
                <Button data-testid="platform-stripe-bootstrap-btn" size="sm"
                  disabled={bootstrapping}
                  onClick={async () => {
                    setBootstrapping(true);
                    try {
                      const { data } = await api.post("/platform/stripe-bootstrap", { confirm: stripe.mode });
                      const created = data.prices.filter((p) => p.action === "created").length;
                      toast.success(`${created} price${created === 1 ? "" : "s"} created in ${data.mode} mode`);
                      load();
                    } catch (e) {
                      toast.error(apiError(e.response?.data?.detail));
                    } finally {
                      setBootstrapping(false);
                    }
                  }}
                  className="bg-amber-400 text-slate-900 hover:bg-amber-300">
                  {bootstrapping ? "Creating…" : `Create the ${stripe.mode} plan prices`}
                </Button>
              </div>
            )}
            {stripe.error && <p className="mt-1 text-xs opacity-90">Stripe said: {stripe.error}</p>}
          </div>
        )}

        {emailError && (
          <div data-testid="platform-email-error"
            className="mb-5 rounded-lg border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-200">
            <strong className="font-semibold">Email delivery problem: </strong>{emailError}
          </div>
        )}

        {s && (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat testId="stat-workspaces" icon={Building2} label="Workspaces" value={s.workspaces}
                sub={`${s.commercial_workspaces} commercial · ${s.internal_workspaces} internal`} />
              <Stat testId="stat-users" icon={Users} label="Users" value={s.users}
                sub={`${s.projects} projects created`} />
              <Stat testId="stat-trials" icon={Clock} label="On trial" value={s.trials_active}
                sub={`${s.trials_expiring_soon} ending within 2 days · ${s.trials_expired} expired`} />
              <Stat testId="stat-conversion" icon={Percent} label="Trial → paid" value={`${s.conversion_rate}%`}
                sub={`${s.conversion_basis.converted} of ${s.conversion_basis.decided} decided · ${s.conversion_basis.still_trialing} still trialing`} />
              <Stat testId="stat-paid" icon={TrendingUp} label="Paying workspaces" value={s.paid_workspaces}
                sub={`${s.paid_seats} seats · churn ${s.churn_rate}%`} />
              <Stat testId="stat-mrr" icon={PoundSterling} label="MRR" value={money(s.mrr_gbp)}
                sub={`ARR ${money(s.arr_gbp)} · ARPA ${money(s.arpa_gbp)}`} />
              <Stat testId="stat-tickets" icon={LifeBuoy} label="Open tickets" value={overview.tickets.open}
                sub={`${overview.tickets.total} raised in total`} />
              <Stat testId="stat-email" icon={MailCheck} label="Email routing"
                value={overview.email_configured ? "Live" : "Off"} sub={overview.support_inbox} />
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-3">
              <div data-testid="platform-signup-chart" className="panel lg:col-span-2 px-5 py-4">
                <p className="text-sm font-semibold text-slate-900">Signups — last 30 days</p>
                <div className="mt-3 w-full min-w-0" style={{ height: 208 }}>
                  <ResponsiveContainer width="99%" height={208}>
                    <BarChart data={overview.signups}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={4} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={26} />
                      <Tooltip />
                      <Bar dataKey="signups" fill="#1F4E79" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div data-testid="platform-sector-mix" className="panel px-5 py-4">
                <p className="text-sm font-semibold text-slate-900">Sector mix</p>
                <div className="mt-3 space-y-2">
                  {Object.entries(s.by_sector).length === 0 && (
                    <p className="text-xs text-slate-500">No commercial workspaces yet.</p>
                  )}
                  {Object.entries(s.by_sector).map(([sector, n]) => (
                    <div key={sector} className="flex items-center gap-2">
                      <span className="w-28 shrink-0 truncate text-xs capitalize text-slate-600">{sector}</span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-[#1F4E79]"
                          style={{ width: `${(n / s.commercial_workspaces) * 100}%` }} />
                      </div>
                      <span className="w-6 text-right text-xs tabular-nums text-slate-500">{n}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {recovery && (
          <div className="mt-6 panel" data-testid="platform-recovery-panel">
            <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-slate-200 px-5 py-3">
              <div>
                <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <ShoppingCart size={15} className="text-slate-400" /> Abandoned baskets — last {recovery.window_days} days
                </p>
                <p className="mt-1 text-xs text-slate-500">{recovery.attribution_note}</p>
              </div>
              <span
                data-testid="platform-recovery-live"
                className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                  recovery.live_sending
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-amber-200 bg-amber-50 text-amber-700"
                }`}
              >
                {recovery.live_sending ? "Chase emails sending" : "Chase emails not enabled"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-px bg-slate-100 sm:grid-cols-3 lg:grid-cols-5">
              {[
                ["recovery-abandoned", "Abandoned", recovery.abandoned, `${money(recovery.value_at_risk_gbp)} at risk`],
                ["recovery-chased", "Chased", recovery.chased, "one email per basket"],
                ["recovery-recovered", "Came back", recovery.recovered, `within ${recovery.attribution_days} days`],
                ["recovery-rate", "Recovery rate", `${recovery.recovery_rate}%`, "of those chased"],
                ["recovery-mrr", "MRR recovered", money(recovery.recovered_mrr_gbp), `${money(recovery.recovered_arr_gbp)} annualised`],
              ].map(([testId, label, value, sub]) => (
                <div key={testId} data-testid={`platform-${testId}`} className="bg-white px-5 py-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</p>
                  <p className="mt-1.5 text-2xl font-bold tabular-nums text-slate-900">{value}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{sub}</p>
                </div>
              ))}
            </div>
            {recovery.rows.length === 0 ? (
              <p className="px-5 py-6 text-sm text-slate-500">
                No abandoned baskets in this window — everyone who reached checkout paid.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table data-testid="platform-recovery-table" className="w-full min-w-[820px] text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-400">
                      <th className="px-5 py-2 font-semibold">Workspace</th>
                      <th className="px-3 py-2 font-semibold">Basket</th>
                      <th className="px-3 py-2 font-semibold">Value</th>
                      <th className="px-3 py-2 font-semibold">Abandoned</th>
                      <th className="px-3 py-2 font-semibold">Chased</th>
                      <th className="px-3 py-2 font-semibold">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recovery.rows.slice(0, 25).map((r) => (
                      <tr key={r.session_id} data-testid={`recovery-row-${r.session_id}`}
                        className="border-t border-slate-100 text-slate-700">
                        <td className="px-5 py-2.5 font-medium text-slate-900">{r.org_name || "—"}</td>
                        <td className="px-3 py-2.5 capitalize">
                          {r.plan} · {r.seats} seat{r.seats === 1 ? "" : "s"} · {r.interval}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums">{money(r.value_gbp)}</td>
                        <td className="px-3 py-2.5 tabular-nums text-slate-500">{day(r.abandoned_at)}</td>
                        <td className="px-3 py-2.5 tabular-nums text-slate-500">{day(r.chased_at)}</td>
                        <td className="px-3 py-2.5">
                          <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${RECOVERY_STATE[r.state]}`}>
                            {r.state}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 panel">
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-5 py-3">
            {BUCKETS.map((b) => (
              <button key={b.key || "all"} data-testid={`platform-filter-${b.key || "all"}`}
                onClick={() => setBucket(b.key)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  bucket === b.key ? "bg-[#1F4E79] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}>
                {b.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <Search size={14} className="text-slate-400" />
              <Input data-testid="platform-search" value={query} placeholder="Workspace or email"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()} className="h-8 w-56 text-xs" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table data-testid="platform-workspaces-table" className="w-full min-w-[1100px] text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  {["Workspace", "Signup email", "Sector", "Plan", "Stage", "Users", "Projects",
                    "Seats", "MRR", "Signed up", "Trial ends", "Last login"].map((h) => (
                    <th key={h} className="px-4 py-2 font-semibold text-slate-900">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-500">No workspaces match.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`platform-row-${r.id}`} className="border-t border-slate-100 text-slate-700">
                    <td className="px-4 py-2 font-medium text-slate-900">
                      {r.org_name}
                      {r.is_test && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-500">test</span>}
                    </td>
                    <td className="px-4 py-2">{r.signup_email || "—"}</td>
                    <td className="px-4 py-2 capitalize">{r.sector || "—"}</td>
                    <td className="px-4 py-2 capitalize">{r.plan}</td>
                    <td className="px-4 py-2">
                      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${BUCKET_STYLE[r.bucket] || ""}`}>
                        {r.bucket === "trialing" && r.trial_days_left != null
                          ? `Trial · ${r.trial_days_left}d left` : r.bucket.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-4 py-2 tabular-nums">{r.users}</td>
                    <td className="px-4 py-2 tabular-nums">{r.projects}</td>
                    <td className="px-4 py-2 tabular-nums">{r.seats || "—"}</td>
                    <td className="px-4 py-2 tabular-nums">{r.mrr_gbp ? money(r.mrr_gbp) : "—"}</td>
                    <td className="px-4 py-2 tabular-nums">{day(r.created_at)}</td>
                    <td className="px-4 py-2 tabular-nums">{day(r.trial_ends_at)}</td>
                    <td className="px-4 py-2 tabular-nums">{day(r.last_login_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
